import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Users,
  Calendar,
  BarChart3,
  CheckCircle2,
  Menu as MenuIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import useGlobalNotifications from "../hooks/useGlobalNotifications";
import { useAccess } from "../contexts/AccessContext";
import { ALL_SIDEBAR_ITEMS } from "../constants/sidebarItems";
import { isFieldVendorLevel } from "../utils/homePath";
import { WhatsAppIcon } from "../simulator/components/icons/BrandIcons";

/**
 * Bottom navigation bar for mobile devices (Ventas app).
 * Shows main sales actions and a menu button to toggle the sidebar.
 * @param {Object} props
 * @param {Function} props.onMenuClick - Function to open the sidebar drawer
 * @returns {JSX.Element}
 */
const MobileBottomNav = ({ onMenuClick }) => {
  const { t } = useTranslation("common");
  const { counters } = useGlobalNotifications();
  const location = useLocation();

  // Nota: `sectionId` NO se usa para nada aquí (`canAccess` ya no decide qué
  // items fijos se ven, ver comentario más abajo); se conserva solo como
  // documentación de a qué sección corresponde cada item fijo.
  const navItems = [
    {
      name: "Contactos",
      path: "/contactos-listas",
      icon: <Users size={24} />,
      badge:
        counters.new_contacts > 0
          ? counters.new_contacts > 99
            ? "99+"
            : counters.new_contacts
          : null,
      isActivePattern: /^\/(contactos-listas|contacto\/)/,
      sectionId: "contactos",
    },
    {
      name: "Tareas",
      path: "/tareas",
      icon: <CheckCircle2 size={24} />,
      sectionId: "tareas",
    },
    {
      name: "Chats",
      path: "/chats",
      icon: <WhatsAppIcon size={24} />,
      badge:
        counters.unread_messages > 0
          ? counters.unread_messages > 99
            ? "99+"
            : counters.unread_messages
          : null,
      isActivePattern: /^\/(chats|chat\/)/,
      sectionId: "chats",
    },
    {
      name: "Calendario",
      path: "/calendario",
      icon: <Calendar size={24} />,
      sectionId: "calendario",
    },
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: <BarChart3 size={24} />,
      sectionId: "dashboard",
    },
  ];

  const { canAccess, userLevel } = useAccess();
  // Los 5 items de siempre son fijos para TODOS los niveles actuales (0-3),
  // sin mirar canAccess por sección: el override real de producción de
  // "dashboard" es 0, y filtrar por canAccess le quitaba el icono de
  // Dashboard a un vendedor normal (regresión real, Hallazgo 1 tarea 7).
  // Solo el rol nuevo (vendedor de campo, nivel en [FIELD_VENDOR_LEVEL,
  // FIELD_VENDOR_LEVEL_MAX], vía `isFieldVendorLevel` — el ÚNICO criterio,
  // ver `utils/homePath.js`) ve en su lugar las secciones que sí puede ver.
  // `userLevel` sale del JWT y se resuelve de forma síncrona (no depende del
  // fetch de configuración, que sí puede tardar ~5s): NO se gatea por
  // `loading`, o durante ese rato el vendedor de campo vería en el móvil los
  // 5 iconos del CRM que no puede abrir. Un JWT sin `level` normaliza a 99,
  // que `isFieldVendorLevel` excluye a propósito → niveles 0-3 (y 99) siguen
  // viendo los 5 items fijos, también durante la carga.
  const isFieldVendor = isFieldVendorLevel(userLevel);

  // `fallback` recorre ALL_SIDEBAR_ITEMS y llama a `canAccess` (que hace
  // `console.log` por invocación) por cada uno: solo tiene sentido calcularlo
  // para el vendedor de campo, que es quien lo usa. Calcularlo en TODOS los
  // renders (incluidos los niveles 0-3, que nunca lo consumen) es trabajo e
  // I/O de consola tirados (Hallazgo E, tarea 7).
  const fallback = isFieldVendor
    ? ALL_SIDEBAR_ITEMS.filter((i) => canAccess(i.id))
        .slice(0, 5)
        .map((i) => ({
          name: i.name,
          path: i.path,
          icon: i.icon,
          sectionId: i.id,
        }))
    : null;
  const visibleItems = isFieldVendor ? fallback : navItems;

  return (
    <nav className="fixed bottom-0 left-0 w-full bg-white/90 dark:bg-gray-900/95 backdrop-blur-lg border-t border-gray-200 dark:border-gray-700/60 min-h-16 flex items-center justify-around px-2 z-40 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] pb-[env(safe-area-inset-bottom)]">
      {visibleItems.map((item) => {
        const isActive = item.isActivePattern
          ? item.isActivePattern.test(location.pathname)
          : location.pathname === item.path;

        return (
          <NavLink
            key={item.name}
            to={item.path}
            aria-current={isActive ? "page" : undefined}
            className={`flex flex-col items-center justify-center flex-1 h-16 relative transition-colors duration-200 ${
              isActive ? "text-red-500 dark:text-red-400" : "text-gray-500 dark:text-gray-400" /* [REVERT-AZUL] text-red-500 dark:text-red-400 */
            }`}
          >
            <div className="relative">
              {item.icon}
              {item.badge && (
                <span className="absolute -top-0.5 -right-1 bg-red-600 text-white text-[10px] font-bold h-4 min-w-[1rem] px-1 rounded-full flex items-center justify-center border border-white"> {/* [REVERT-AZUL] bg-red-600 */}
                  {item.badge}
                </span>
              )}
            </div>
            <span className="text-[10px] mt-1 font-medium">{item.name}</span>
            {isActive && (
              <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-red-600 rounded-b-full shadow-[0_2px_8px_rgba(220,38,38,0.4)]" />
            )}
          </NavLink>
        );
      })}

      <button
        onClick={onMenuClick}
        className="flex flex-col items-center justify-center flex-1 h-16 text-gray-500 dark:text-gray-400 active:bg-gray-50 dark:active:bg-gray-800 transition-colors duration-200"
        aria-label={t("menu")}
      >
        <MenuIcon size={24} />
        <span className="text-[10px] mt-1 font-medium">
          {t("more") || "Más"}
        </span>
      </button>
    </nav>
  );
};

export default MobileBottomNav;
